import { useState, useEffect, useRef } from 'react'
import { useComments } from '../queries/useComments.js'
import { useUIStore } from '../store/uiStore.js'
import { timeAgo } from '../data/metaKeys.js'
import { colorFor } from '../data/userColors.js'
import { firstUrl, parseSegments } from '../data/linkDetection.js'
import MentionTextarea from './MentionTextarea.jsx'
import EmojiPickerButton from './EmojiPickerButton.jsx'
import LinkPreviewCard from './LinkPreviewCard.jsx'

const TEAM = [
  'Ricky', 'Zoe', 'Christina', 'Katie', 'Margo', 'Emily',
  'Sandra', 'Sergina', 'Jonathan', 'Chris', 'Harold',
  'Jessie', 'Christine', 'Braden', 'Colin', 'Jane',
]

/**
 * CommentPanel — slide-in panel from the right showing all threads for a
 * given context (goal or sub-goal), plus a form to start a new thread.
 *
 * Driven by uiStore.activeCommentContext. Mounted at the app root so it
 * overlays everything.
 */
export default function CommentPanel() {
  const activeContext = useUIStore((s) => s.activeCommentContext)
  const closePanel = useUIStore((s) => s.closeCommentPanel)
  const currentUser = useUIStore((s) => s.currentUser)

  // Only render when there's an active context
  const contextLabel = activeContext?.label
  const contextType = activeContext?.type || ''

  const { threads, createThread, addReply, setResolved, deleteThread } = useComments(contextLabel)

  const [newText, setNewText] = useState('')
  const [replyingTo, setReplyingTo] = useState(null)
  const [replyText, setReplyText] = useState('')
  const [filter, setFilter] = useState('all') // 'all' | 'open' | 'resolved'
  const newTextareaRef = useRef(null)

  // Close on Escape
  useEffect(() => {
    if (!activeContext) return
    const onKey = (e) => {
      if (e.key === 'Escape') closePanel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [activeContext, closePanel])

  if (!activeContext) return null

  const filtered =
    filter === 'open' ? threads.filter((t) => !t.resolved)
    : filter === 'resolved' ? threads.filter((t) => t.resolved)
    : threads

  const handleSubmit = () => {
    if (!newText.trim()) return
    createThread({ text: newText, contextType })
    setNewText('')
  }

  return (
    <>
      {/* Backdrop — click outside to close */}
      <div
        onClick={closePanel}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.1)',
          zIndex: 890,
        }}
      />

      {/* Panel */}
      <aside
        style={{
          position: 'fixed',
          right: 0,
          top: 0,
          width: 360,
          height: '100vh',
          background: '#fff',
          boxShadow: '-4px 0 16px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 900,
          fontFamily: 'inherit',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '14px 16px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)' }}>
              💬 Comments
            </span>
            <button
              onClick={closePanel}
              title="Close"
              style={{
                marginLeft: 'auto',
                background: 'transparent',
                border: 'none',
                fontSize: 18,
                color: 'var(--muted-light)',
                cursor: 'pointer',
                padding: 0,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: 'var(--muted)',
              textTransform: 'uppercase',
              letterSpacing: 0.06,
            }}
          >
            {contextType || 'Context'}
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
            {contextLabel}
          </div>

          {/* Filter */}
          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
            {['all', 'open', 'resolved'].map((k) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                style={{
                  flex: 1,
                  padding: '4px 8px',
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.06,
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  background: filter === k ? 'var(--accent-bg)' : '#fff',
                  color: filter === k ? 'var(--accent)' : 'var(--muted)',
                  cursor: 'pointer',
                }}
              >
                {k}
              </button>
            ))}
          </div>
        </div>

        {/* Thread list */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 12,
          }}
        >
          {filtered.length === 0 ? (
            <div
              style={{
                fontSize: 12,
                color: 'var(--muted-light)',
                fontStyle: 'italic',
                textAlign: 'center',
                padding: '20px 0',
              }}
            >
              {threads.length === 0
                ? 'No comments yet. Start a thread below.'
                : `No ${filter} threads.`}
            </div>
          ) : (
            filtered.map((thread) => (
              <ThreadCard
                key={thread.id}
                thread={thread}
                replyingTo={replyingTo}
                replyText={replyText}
                onReplyStart={() => {
                  setReplyingTo(thread.id)
                  setReplyText('')
                }}
                onReplyCancel={() => {
                  setReplyingTo(null)
                  setReplyText('')
                }}
                onReplyChange={setReplyText}
                onReplySubmit={() => {
                  addReply(thread.id, replyText)
                  setReplyText('')
                  setReplyingTo(null)
                }}
                onToggleResolved={() => setResolved(thread.id, !thread.resolved)}
                onDelete={() => {
                  if (confirm('Delete this comment thread?')) deleteThread(thread.id)
                }}
                currentUser={currentUser}
              />
            ))
          )}
        </div>

        {/* New comment composer */}
        <div
          style={{
            borderTop: '1px solid var(--border)',
            padding: 12,
            background: '#fafafa',
          }}
        >
          <MentionTextarea
            ref={newTextareaRef}
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit()
            }}
            placeholder="Write a comment… type @ to mention a teammate"
            pickerDirection="above"
            style={{
              width: '100%',
              minHeight: 60,
              padding: '8px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 12,
              fontFamily: 'inherit',
              resize: 'vertical',
              outline: 'none',
            }}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 6,
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <EmojiPickerButton
                textareaRef={newTextareaRef}
                value={newText}
                setValue={setNewText}
                direction="above"
              />
              <span style={{ fontSize: 10, color: 'var(--muted-light)' }}>
                Cmd+Enter to post
              </span>
            </div>
            <button
              onClick={handleSubmit}
              disabled={!newText.trim()}
              style={{
                padding: '6px 14px',
                border: 'none',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 700,
                background: newText.trim() ? 'var(--accent)' : '#e5e7eb',
                color: newText.trim() ? '#fff' : '#9ca3af',
                cursor: newText.trim() ? 'pointer' : 'not-allowed',
              }}
            >
              Post
            </button>
          </div>
        </div>
      </aside>
    </>
  )
}

/* ─── Thread card ───────────────────────────────────────────────────── */

function ThreadCard({
  thread,
  replyingTo,
  replyText,
  onReplyStart,
  onReplyCancel,
  onReplyChange,
  onReplySubmit,
  onToggleResolved,
  onDelete,
  currentUser,
}) {
  const isReplying = replyingTo === thread.id
  const isResolved = thread.resolved
  const replies = thread.replies || []
  const replyTextareaRef = useRef(null)

  return (
    <div
      style={{
        marginBottom: 12,
        padding: 10,
        background: isResolved ? '#f3f4f6' : '#fff',
        border: `1px solid ${isResolved ? 'var(--border)' : '#e5e7eb'}`,
        borderRadius: 8,
        opacity: isResolved ? 0.7 : 1,
      }}
    >
      {/* Thread header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 8,
          fontSize: 10,
          color: 'var(--muted)',
          fontWeight: 600,
        }}
      >
        {isResolved && (
          <span
            style={{
              background: '#f0fdf4',
              color: '#166534',
              border: '1px solid #bbf7d0',
              borderRadius: 4,
              padding: '1px 6px',
              fontSize: 9,
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            ✓ Resolved
          </span>
        )}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button
            onClick={onToggleResolved}
            title={isResolved ? 'Reopen thread' : 'Mark as resolved'}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--muted)',
              fontSize: 10,
              fontWeight: 600,
              cursor: 'pointer',
              textDecoration: 'underline',
              textDecorationStyle: 'dotted',
            }}
          >
            {isResolved ? '↩ Reopen' : '✓ Resolve'}
          </button>
          <button
            onClick={onDelete}
            title="Delete thread"
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--muted-light)',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </span>
      </div>

      {/* Replies */}
      {replies.map((reply) => (
        <ReplyItem key={reply.id} reply={reply} currentUser={currentUser} />
      ))}

      {/* Reply action */}
      {!isReplying && !isResolved && (
        <button
          onClick={onReplyStart}
          style={{
            marginTop: 4,
            background: 'none',
            border: 'none',
            color: 'var(--accent)',
            fontSize: 11,
            fontWeight: 600,
            cursor: 'pointer',
            padding: 0,
            textDecoration: 'underline',
            textDecorationStyle: 'dotted',
          }}
        >
          ↳ Reply
        </button>
      )}

      {/* Reply composer */}
      {isReplying && (
        <div style={{ marginTop: 8 }}>
          <MentionTextarea
            ref={replyTextareaRef}
            autoFocus
            value={replyText}
            onChange={(e) => onReplyChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onReplyCancel()
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onReplySubmit()
            }}
            placeholder="Reply… type @ to mention"
            pickerDirection="above"
            style={{
              width: '100%',
              minHeight: 50,
              padding: '6px 8px',
              border: '1px solid var(--accent)',
              borderRadius: 6,
              fontSize: 11,
              fontFamily: 'inherit',
              resize: 'vertical',
              outline: 'none',
            }}
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' }}>
            <EmojiPickerButton
              textareaRef={replyTextareaRef}
              value={replyText}
              setValue={onReplyChange}
              direction="above"
              size={24}
            />
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button
                onClick={onReplyCancel}
                style={{
                  padding: '4px 10px',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  background: '#fff',
                  color: 'var(--muted)',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={onReplySubmit}
                disabled={!replyText.trim()}
                style={{
                  padding: '4px 12px',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 700,
                  background: replyText.trim() ? 'var(--accent)' : '#e5e7eb',
                  color: replyText.trim() ? '#fff' : '#9ca3af',
                  cursor: replyText.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                Reply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── Single reply ──────────────────────────────────────────────────── */

function ReplyItem({ reply, currentUser }) {
  const authorColor = colorFor(reply.author)
  const when = reply.at ? timeAgo(new Date(reply.at).getTime()) : ''
  const previewUrl = firstUrl(reply.text)

  return (
    <div style={{ marginBottom: 8, fontSize: 11 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: authorColor,
            color: '#fff',
            fontSize: 9,
            fontWeight: 800,
          }}
        >
          {reply.author?.[0] || '?'}
        </span>
        <span style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>
          {reply.author || 'Anonymous'}
        </span>
        {when && (
          <span style={{ color: 'var(--muted-light)', fontSize: 10 }}>{when}</span>
        )}
      </div>
      <div
        style={{
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          paddingLeft: 24,
        }}
      >
        {renderTextWithMentions(reply.text, currentUser)}
      </div>
      {previewUrl && (
        <div style={{ paddingLeft: 24, marginTop: 4 }}>
          <LinkPreviewCard url={previewUrl} compact />
        </div>
      )}
    </div>
  )
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

// Turn @name references into inline blue pills and bare URLs into
// clickable links. If @name matches a TEAM member we render a pill;
// otherwise the text is left alone.
function renderTextWithMentions(text, currentUser) {
  if (!text) return null
  const parts = []
  let lastIdx = 0
  const regex = /@([A-Za-z][A-Za-z0-9_]+)/g
  let match
  while ((match = regex.exec(text)) !== null) {
    const [full, name] = match
    const start = match.index
    const teamName = TEAM.find((t) => t.toLowerCase() === name.toLowerCase())
    if (!teamName) continue
    if (start > lastIdx) parts.push(...linkifySegment(text.slice(lastIdx, start), parts.length))
    const isMe = teamName === currentUser
    parts.push(
      <span
        key={`mention-${start}-${name}`}
        style={{
          display: 'inline-block',
          background: isMe ? '#fef3c7' : '#dbeafe',
          color: isMe ? '#92400e' : '#1e40af',
          padding: '0 5px',
          borderRadius: 4,
          fontSize: 10,
          fontWeight: 700,
        }}
      >
        @{teamName}
      </span>
    )
    lastIdx = start + full.length
  }
  if (lastIdx < text.length) {
    parts.push(...linkifySegment(text.slice(lastIdx), parts.length))
  }
  return parts.length > 0 ? parts : linkifySegment(text, 0)
}

// Break a text fragment into text + anchor spans. Used by
// renderTextWithMentions so URLs inside comments are clickable.
function linkifySegment(fragment, keyBase) {
  if (!fragment) return []
  return parseSegments(fragment).map((seg, i) =>
    seg.type === 'url' ? (
      <a
        key={`url-${keyBase}-${i}`}
        href={seg.value}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: 'var(--accent)',
          textDecoration: 'underline',
          wordBreak: 'break-all',
        }}
      >
        {seg.value}
      </a>
    ) : (
      <span key={`t-${keyBase}-${i}`}>{seg.value}</span>
    )
  )
}

